import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

// Per Quoting_markdown/cushions.md — structurally mirrors
// BolsterPillowForm.tsx, minus Ruffle (not part of this SOP) and with an
// explicit Length/Depth/Thickness three-dimension layout instead of
// Width/Height. Length=width_inches, Depth=height_inches,
// Thickness=depth_inches (matches cushionCalculations.ts's mapping). Trim
// here is a plain Yes/No, stored in the shared trim_placement column as
// 'bottom' (a sentinel; cushions have no placement concept), same
// convention as pillow/sham/bolster.

type WeltType = 'none' | 'self' | 'contrast';
type InsertType = 'dacron_foam' | 'down_wrapped' | 'down' | '';

interface State {
  widthInches: string;   // Length
  heightInches: string;  // Depth
  depthInches: string;   // Thickness
  fabricDescription: string;
  patternRepeatInches: string;
  weltType: WeltType;
  trimOn: boolean;
  trimDescription: string;
  insertType: InsertType;
}

const CushionForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [state, setState] = useState<State>({
    widthInches: iv?.widthInches != null ? String(iv.widthInches) : '',
    heightInches: iv?.heightInches != null ? String(iv.heightInches) : '',
    depthInches: iv?.depthInches != null ? String(iv.depthInches) : '',
    fabricDescription: iv?.fabricDescription || '',
    patternRepeatInches: iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '',
    weltType: (iv?.weltType as WeltType) || 'none',
    trimOn: !!iv?.trimPlacement && iv.trimPlacement !== 'none',
    trimDescription: iv?.trimDescription || '',
    insertType: (iv?.insertType as InsertType) || '',
  });
  const [error, setError] = useState<string | null>(null);
  const s = (patch: Partial<State>) => setState((prev) => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const length = parseFloat(state.widthInches);
      const depth = parseFloat(state.heightInches);
      const thickness = parseFloat(state.depthInches);
      if (!length || length <= 0 || !depth || depth <= 0 || !thickness || thickness <= 0) {
        setError('Length, Depth, and Thickness must be greater than 0');
        return null;
      }
      setError(null);
      return {
        widthInches: length,
        heightInches: depth,
        depthInches: thickness,
        fabricDescription: state.fabricDescription.trim() || null,
        patternRepeatInches: state.patternRepeatInches ? parseFloat(state.patternRepeatInches) : null,
        weltType: state.weltType,
        trimPlacement: state.trimOn ? 'bottom' : 'none',
        trimDescription: state.trimDescription.trim() || null,
        insertType: state.insertType || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Length (inches)</label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.widthInches}
            onChange={(e) => s({ widthInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Depth (inches)</label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.heightInches}
            onChange={(e) => s({ heightInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Thickness (inches)</label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.depthInches}
            onChange={(e) => s({ depthInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Pattern Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number" min="0" step="0.25" className="input"
            placeholder="None"
            value={state.patternRepeatInches}
            onChange={(e) => s({ patternRepeatInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={state.fabricDescription}
            onChange={(name) => s({ fabricDescription: name })}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Welt</label>
          <select
            className="input"
            value={state.weltType}
            onChange={(e) => s({ weltType: e.target.value as WeltType })}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="self">Self Welt</option>
            <option value="contrast">Contrast Welt</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer', padding: '9px 0' }}>
            <input
              type="checkbox"
              checked={state.trimOn}
              onChange={(e) => s({ trimOn: e.target.checked })}
              disabled={props.disabled}
            />
            Add trim
          </label>
        </div>
        <TrimNameInput value={state.trimDescription} onChange={(v) => s({ trimDescription: v })} disabled={props.disabled} />
        <div className="form-group" style={{ flex: 1 }}>
          <label>Insert Type</label>
          <select
            className="input"
            value={state.insertType}
            onChange={(e) => s({ insertType: e.target.value as InsertType })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="dacron_foam">Dacron Wrapped Foam</option>
            <option value="down_wrapped">Down Wrapped Foam</option>
            <option value="down">Down</option>
          </select>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default CushionForm;
